/**
 * WebSocket Client for Real-time Notifications
 *
 * Connects to the backend WebSocket server to receive real-time notifications
 */

export type NotificationMessage =
  | {
      type: 'notification';
      id?: string;
      user_id?: string;
      title?: string;
      body?: string;
      notification_type?: string;
      is_read?: boolean;
      created_at?: number;
      reference_type?: string;
      reference_id?: string;
      action_url?: string;
      message?: string;
      timestamp?: number;
    }
  | {
      type: 'connected';
      message?: string;
      timestamp?: number;
    }
  | {
      type: 'error';
      message?: string;
      timestamp?: number;
    }
  | MessageReadEvent
  | MessagesAllReadEvent
  | MessageUpdatedEvent
  | MessageDeletedEvent
  | {
      type: 'new_message';
      conversation_id: string;
      message: {
        id: string;
        conversation_id: string;
        sender_id: string;
        sender?: {
          id: string;
          full_name: string;
          avatar: string;
          user_type?: string;
        };
        content: string;
        message_type: string;
        attachments: unknown[];
        is_read: boolean;
        is_system_message: boolean;
        created_at: number;
        updated_at: number;
      };
    };

export type MessageReadEvent = {
  type: 'message_read';
  conversation_id: string;
  message_id: string;
  read_by: string;
  read_at: number;
};

export type MessagesAllReadEvent = {
  type: 'messages_all_read';
  conversation_id: string;
  read_by: string;
  read_at: number;
};

export type MessageUpdatedEvent = {
  type: 'message_updated';
  conversation_id: string;
  message: {
    id: string;
    conversation_id: string;
    sender_id: string;
    content: string;
    updated_at: number;
  };
};

export type MessageDeletedEvent = {
  type: 'message_deleted';
  conversation_id: string;
  message_id: string;
};

export type MessageEvent =
  | Extract<NotificationMessage, { type: 'new_message' }>
  | MessageReadEvent
  | MessagesAllReadEvent
  | MessageUpdatedEvent
  | MessageDeletedEvent;

type NotificationHandler = (notification: NotificationMessage) => void;
type MessageHandler = (message: MessageEvent) => void;
type ConnectionHandler = () => void;
type ErrorHandler = (error: Event | Error) => void;

export class NotificationWebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isConnecting = false;
  private isManuallyClosed = false;

  // Event handlers
  private notificationHandlers: Set<NotificationHandler> = new Set();
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private disconnectHandlers: Set<ConnectionHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();

  constructor(baseUrl: string) {
    // Convert HTTP/HTTPS URL to WS/WSS URL
    this.url = this.convertToWebSocketUrl(baseUrl);
  }

  /**
   * Convert HTTP/HTTPS URL to WebSocket URL
   */
  private convertToWebSocketUrl(url: string): string {
    // Remove trailing slash
    url = url.replace(/\/$/, '');

    // If already a WebSocket URL, just append the path
    if (url.startsWith('wss://')) {
      // Remove /api if it exists at the end to avoid duplication
      const baseUrl = url.replace(/\/api$/, '');
      return `${baseUrl}/api/v1/notifications/ws`;
    } else if (url.startsWith('ws://')) {
      // Remove /api if it exists at the end to avoid duplication
      const baseUrl = url.replace(/\/api$/, '');
      return `${baseUrl}/api/v1/notifications/ws`;
    }

    // Convert http:// to ws:// and https:// to wss://
    if (url.startsWith('https://')) {
      // Remove /api if it exists at the end to avoid duplication
      const baseUrl = url.replace(/\/api$/, '');
      return baseUrl.replace('https://', 'wss://') + '/api/v1/notifications/ws';
    } else if (url.startsWith('http://')) {
      // Remove /api if it exists at the end to avoid duplication
      const baseUrl = url.replace(/\/api$/, '');
      return baseUrl.replace('http://', 'ws://') + '/api/v1/notifications/ws';
    }

    // If no protocol, assume ws:// for development
    // Remove /api if it exists at the end to avoid duplication
    const baseUrl = url.replace(/\/api$/, '');
    return `ws://${baseUrl}/api/v1/notifications/ws`;
  }

  /**
   * Set authentication token
   */
  setToken(token: string | null): void {
    this.token = token;
    // If already connected, reconnect with new token
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.disconnect();
      this.connect();
    }
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    if (!this.token) {
      console.warn('WebSocket: No token available, cannot connect');
      return;
    }

    this.isConnecting = true;
    this.isManuallyClosed = false;

    try {
      // Note: WebSocket API doesn't support custom headers, so we use query parameter
      // Backend middleware should be updated to accept token from query parameter for WebSocket
      // For now, using query parameter as a workaround
      const wsUrl = `${this.url}?token=${encodeURIComponent(this.token)}`;
      console.log('WebSocket: Attempting to connect to', wsUrl.replace(/\?token=.*/, '?token=***'));
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket: Connected to notification server');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;

        // Notify connection handlers
        this.connectionHandlers.forEach((handler) => handler());
      };

      this.ws.onmessage = (event) => {
        // Some servers may batch multiple JSON objects in one frame separated by newlines
        const payload = typeof event.data === 'string' ? event.data : '';
        const chunks = payload.split('\n').filter((c) => c.trim().length > 0);

        chunks.forEach((chunk) => {
          try {
            const data = JSON.parse(chunk) as NotificationMessage;
            this.handleMessage(data);
          } catch (error) {
            console.error('WebSocket: Failed to parse message chunk', { chunk, error });
          }
        });
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket: Connection error', {
          url: this.url,
          readyState: this.ws?.readyState,
          error: error,
        });
        // Log more details if available
        if (this.ws) {
          console.error('WebSocket: Ready state:', {
            CONNECTING: WebSocket.CONNECTING,
            OPEN: WebSocket.OPEN,
            CLOSING: WebSocket.CLOSING,
            CLOSED: WebSocket.CLOSED,
            current: this.ws.readyState,
          });
        }
        this.isConnecting = false;
        this.errorHandlers.forEach((handler) => handler(error));
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket: Connection closed', {
          code: event.code,
          reason: event.reason || 'No reason provided',
          wasClean: event.wasClean,
          url: this.url,
        });
        this.isConnecting = false;
        this.ws = null;

        // Notify disconnect handlers
        this.disconnectHandlers.forEach((handler) => handler());

        // Attempt to reconnect if not manually closed
        if (!this.isManuallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('WebSocket: Failed to create connection', error);
      this.isConnecting = false;
      if (error instanceof Error) {
        this.errorHandlers.forEach((handler) => handler(error));
      }
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isManuallyClosed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(
      `WebSocket: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isManuallyClosed && this.token) {
        this.connect();
      }
    }, delay);
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(message: NotificationMessage): void {
    if (message.type === 'notification') {
      // Notify all notification handlers
      this.notificationHandlers.forEach((handler) => handler(message));
    } else if (message.type === 'new_message') {
      this.messageHandlers.forEach((handler) => handler(message));
    } else if (
      message.type === 'message_read' ||
      message.type === 'messages_all_read' ||
      message.type === 'message_updated' ||
      message.type === 'message_deleted'
    ) {
      this.messageHandlers.forEach((handler) => handler(message as unknown as MessageEvent));
    } else if (message.type === 'connected') {
      console.log('WebSocket: Server confirmed connection', message.message);
    } else if (message.type === 'error') {
      console.error('WebSocket: Server error', message.message);
      this.errorHandlers.forEach((handler) =>
        handler(new Error(message.message || 'Unknown error'))
      );
    }
  }

  /**
   * Subscribe to notification events
   */
  onNotification(handler: NotificationHandler): () => void {
    this.notificationHandlers.add(handler);
    // Return unsubscribe function
    return () => {
      this.notificationHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to new message events
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to connection events
   */
  onConnect(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to disconnect events
   */
  onDisconnect(handler: ConnectionHandler): () => void {
    this.disconnectHandlers.add(handler);
    return () => {
      this.disconnectHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to error events
   */
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  /**
   * Get connection state
   */
  getState(): 'connecting' | 'open' | 'closed' {
    if (this.isConnecting) {
      return 'connecting';
    }
    if (this.ws) {
      switch (this.ws.readyState) {
        case WebSocket.CONNECTING:
          return 'connecting';
        case WebSocket.OPEN:
          return 'open';
        default:
          return 'closed';
      }
    }
    return 'closed';
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
